// The home page: is the network healthy, and if not, where do I click.
//
// Everything on it answers one of those two questions and links to the page
// that acts on the answer. It reads one endpoint, /v1/reports/overview, so the
// 30-second refresh costs a handful of queries however many widgets render.
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api, type Me, type Overview as OverviewData } from "../api";
import { BURN_COLOR, CHART } from "../chartTheme";
import { Card, Empty, ErrorNote, PageHeader, SegmentBar, Spinner, Stat, StatusDot } from "../ui";
import type { Tone } from "../ui";

const STATE_TONES: Record<string, Tone> = {
  active: "ok",
  provisioned: "info",
  inventory: "idle",
  suspended: "warn",
  revoked: "crit",
  retired: "idle",
};

export default function Overview({ me }: { me: Me }) {
  const { data, isLoading, error } = useQuery<OverviewData>({
    queryKey: ["overview"],
    queryFn: () => api.get("/v1/reports/overview"),
    refetchInterval: 30_000,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const { fleet, alerts, origins } = data;
  const alertTotal = Object.values(alerts).reduce((a, b) => a + b, 0);
  const degradedSources = fleet.sources_total - fleet.sources_ok;

  return (
    <>
      <PageHeader
        eyebrow="NetComTV distribution network"
        title={me.role === "reseller" ? `${me.reseller_name ?? "Your"} network` : "Network overview"}
        subtitle="Live from the last check-in of every Outpost. Click any figure to act on it."
      />

      {/* The verdict row: four figures that say whether anyone needs to do
          anything. Each is a link to the page where they would do it. */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Link to="/fleet">
          <Stat
            label="Outposts online"
            value={`${fleet.online}/${fleet.total}`}
            tone={fleet.offline ? "crit" : "ok"}
            hint={fleet.offline ? `${fleet.offline} active but silent` : "all active boxes reporting"}
          />
        </Link>
        <Link to="/fleet">
          <Stat
            label="Channels receiving"
            value={fleet.sources_total ? `${fleet.sources_ok}/${fleet.sources_total}` : "—"}
            tone={fleet.sources_total === 0 ? "idle" : degradedSources ? "warn" : "ok"}
            hint={
              fleet.sources_total === 0
                ? "no channels provisioned yet"
                : degradedSources
                  ? `${degradedSources} degraded`
                  : fleet.on_backup
                    ? `${fleet.on_backup} on backup origin`
                    : "all on primary origins"
            }
          />
        </Link>
        <Link to="/alerts">
          <Stat
            label="Open alerts"
            value={alertTotal}
            tone={alerts.P1 ? "crit" : alerts.P2 ? "warn" : alertTotal ? "info" : "ok"}
            hint={
              alertTotal
                ? ["P1", "P2", "P3"]
                    .filter((s) => alerts[s])
                    .map((s) => `${alerts[s]} ${s}`)
                    .join(" · ")
                : "nothing firing"
            }
          />
        </Link>
        <Link to="/fleet">
          <Stat
            label="Config pending"
            value={fleet.config_pending}
            tone={fleet.config_pending ? "info" : "idle"}
            hint={fleet.config_pending ? "applies at the next check-in" : "fleet matches desired state"}
          />
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Fleet by state" actions={<Link to="/fleet" className="btn-ghost">Fleet →</Link>}>
          <SegmentBar
            ariaLabel={`${fleet.total} Outposts by lifecycle state`}
            segments={Object.entries(fleet.by_state).map(([state, count]) => ({
              label: state,
              count,
              tone: STATE_TONES[state] ?? "idle",
            }))}
          />
          {Object.keys(fleet.agent_versions).length > 0 && (
            <div className="mt-4 border-t border-ink-700 pt-3 text-xs text-ink-400">
              Agent versions:{" "}
              {Object.entries(fleet.agent_versions)
                .sort((a, b) => b[1] - a[1])
                .map(([version, count]) => `${version} ×${count}`)
                .join(" · ")}
            </div>
          )}
        </Card>

        {origins ? (
          <Card
            title="Origin egress"
            actions={<Link to="/origin" className="btn-ghost">Origin health →</Link>}
            padded={false}
          >
            {origins.dvrs.length === 0 ? (
              <Empty>No distribution load yet — no channel has been pulled.</Empty>
            ) : (
              <figure className="p-4">
                <div className="h-44 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={origins.dvrs.map((d) => ({
                        host: d.dvr_host.split(".")[0],
                        mbps: d.reported_mbps,
                        burn: d.burn,
                      }))}
                      margin={{ top: 18, right: 8, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid stroke={CHART.grid} vertical={false} />
                      <XAxis
                        dataKey="host"
                        tick={{ fill: CHART.axis, fontSize: 12 }}
                        axisLine={{ stroke: CHART.grid }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fill: CHART.axis, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                      />
                      <Tooltip
                        cursor={{ fill: "#ffffff08" }}
                        contentStyle={{
                          background: CHART.surface,
                          border: `1px solid ${CHART.grid}`,
                          borderRadius: 6,
                          fontSize: 12,
                          color: CHART.ink,
                        }}
                        formatter={(v: number) => [`${v} Mbps`, "egress"]}
                      />
                      <Bar dataKey="mbps" radius={[4, 4, 0, 0]} maxBarSize={44}>
                        {origins.dvrs.map((d) => (
                          <Cell key={d.dvr_host} fill={BURN_COLOR[d.burn]} />
                        ))}
                        <LabelList
                          dataKey="mbps"
                          position="top"
                          fill={CHART.inkBody}
                          fontSize={11}
                          formatter={(v: number) => (v > 0 ? v : "")}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <figcaption className="mt-2 text-xs text-ink-400">
                  {origins.total_mbps} Mbps across {origins.total_sessions} distribution session
                  {origins.total_sessions === 1 ? "" : "s"} · thresholds {origins.thresholds.warn_mbps}/
                  {origins.thresholds.critical_mbps} Mbps
                </figcaption>
              </figure>
            )}
          </Card>
        ) : (
          <Card title="Your service">
            <div className="space-y-3 text-sm text-ink-300">
              <div className="flex items-center justify-between">
                <span>Equipment</span>
                <Link to="/fleet" className="text-brand-on hover:underline">
                  {fleet.total} Outpost{fleet.total === 1 ? "" : "s"} →
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <span>Channels receiving</span>
                <span className="font-mono">
                  {fleet.sources_ok}/{fleet.sources_total}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Billing</span>
                <Link to="/billing" className="text-brand-on hover:underline">
                  Invoices →
                </Link>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* One line of truth about the enforcement chain, because a green fleet
          with a failed auth probe is money leaking, not health. */}
      {origins && (
        <div className="callout mt-6 text-sm text-ink-300">
          <StatusDot
            tone={fleet.sources_total > 0 ? "ok" : "idle"}
            label={
              fleet.sources_total > 0
                ? "Entitlement enforcement is in the path of every pull; see Origin health for the nightly probe."
                : "No streams are flowing yet — provision a channel to exercise the entitlement path."
            }
          />
        </div>
      )}
    </>
  );
}
